import { useMemo, lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { QueryClientProvider } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { queryClient } from '@/lib/query-client';
import { createQueryCacheStorage, QUERY_CACHE_KEY } from '@/lib/query-cache-storage';
import i18n from '@erp/i18n';
import { createSupabaseServices } from '@erp/data-access/supabase-adapter';
import type { AppServices } from '@erp/data-access/interfaces';
import { ServiceProvider } from '@/lib/service-context';
import { AuthProvider, useAuth } from '@/lib/auth-context';
import { BrandThemeProvider } from '@/lib/brand-theme-provider';
import { NavPrefsProvider } from '@/lib/nav-prefs/nav-prefs-context';
import { NativeBridgeProvider, useNativeBridge } from '@/lib/native-context';
import { DesktopShellHooks } from '@/lib/desktop-shell-hooks';
import { createSessionStorageBridge } from '@/lib/session-storage-bridge';
import { SyncProvider } from '@/lib/sync-context';
import { InitialSyncProvider, InitialSyncGate } from '@/lib/initial-sync-context';
import { InitialSyncSplash } from '@/components/InitialSyncSplash';
import { readBootHintSync } from '@/lib/boot-hint';
import { BrandBootBlank } from '@/components/BrandSplash';
import { AppErrorBoundary } from '@/components/AppErrorBoundary';
import { AppLayout } from '@/components/layout/AppLayout';
import { ModuleGuard } from '@/components/ModuleGuard';
import { PlatformAdminLayout } from '@/pages/platform-admin/PlatformAdminLayout';
import {
  PlatformAdminClientsPage,
  PlatformAdminDashboardPage,
  PlatformAdminProPage,
  PlatformAdminRevenuePage,
  PlatformAdminRisksPage,
  PlatformAdminStorefrontPage,
} from '@/pages/platform-admin/PlatformAdminDashboardPage';
import { LoginPage } from '@/pages/LoginPage';
import { SignupPage } from '@/pages/SignupPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { UpdatePasswordPage } from '@/pages/UpdatePasswordPage';
import { OnboardingWizard } from '@/pages/OnboardingWizard';
import { RequirePermission } from '@/components/auth/RequirePermission';
import { usePlatformAdminAccess } from '@/hooks/use-platform-admin';
import { isPlatformAdminHost, isPlatformAdminMode } from '@/lib/platform-admin';
import {
  ADMIN_TENANT_VIEW_SESSION_CLEARED_EVENT,
  clearPlatformAdminTenantViewSession,
  hasPlatformAdminTenantViewExternalAuth,
} from '@/lib/admin-tenant-view';
import { useCurrentTenant } from '@/hooks/use-tenant';
import { DashboardPage } from '@/pages/DashboardPage';
import { BrandingPage } from '@/pages/BrandingPage';
import { ArticlesPage } from '@/pages/articles/ArticlesPage';
import { ArticleProfilePage } from '@/pages/articles/ArticleProfilePage';
import { CategoriesPage } from '@/pages/articles/CategoriesPage';
import { FamiliesPage } from '@/pages/articles/FamiliesPage';
import { BrandsPage } from '@/pages/articles/BrandsPage';
import { ClientsPage } from '@/pages/clients/ClientsPage';
import { ClientProfilePage } from '@/pages/clients/ClientProfilePage';
import { RepresentativesPage } from '@/pages/representatives/RepresentativesPage';
import { RepresentativeProfilePage } from '@/pages/representatives/RepresentativeProfilePage';
import { SuppliersPage } from '@/pages/suppliers/SuppliersPage';
import { SupplierProfilePage } from '@/pages/suppliers/SupplierProfilePage';
import { SalesDocumentList } from '@/pages/sales/SalesDocumentList';
import { SalesDocumentPage } from '@/pages/sales/SalesDocumentPage';
import { SettingsHubPage } from '@/pages/settings/SettingsHubPage';
import { CompanySettingsPage } from '@/pages/settings/CompanySettingsPage';
import { AppBuilderPage } from '@/pages/app-builder/AppBuilderPage';
import { ArticleTicketSettingsPage } from '@/pages/settings/ArticleTicketSettingsPage';
import { QuickLinksSettingsPage } from '@/pages/settings/QuickLinksSettingsPage';
import { ModulesSettingsPage } from '@/pages/settings/ModulesSettingsPage';
import { DeliveryProvidersListPage } from '@/pages/settings/delivery-providers/DeliveryProvidersListPage';
import { DeliveryProviderProfilePage } from '@/pages/settings/delivery-providers/DeliveryProviderProfilePage';

if (typeof document !== 'undefined') {
  document.documentElement.dataset.i18nReady = i18n.isInitialized ? 'true' : 'pending';
  i18n.on('initialized', () => {
    document.documentElement.dataset.i18nReady = 'true';
  });
}

// Phase 2 — lazy loaded
const PurchaseDocumentList = lazy(() => import('@/pages/purchases/PurchaseDocumentList').then(m => ({ default: m.PurchaseDocumentList })));
const PurchaseDocumentPage = lazy(() => import('@/pages/purchases/PurchaseDocumentPage').then(m => ({ default: m.PurchaseDocumentPage })));
const StockPage = lazy(() => import('@/pages/stock/StockPage').then(m => ({ default: m.StockPage })));
const StockVehicleDocumentsPage = lazy(() => import('@/pages/stock/StockVehicleDocumentsPage').then(m => ({ default: m.StockVehicleDocumentsPage })));
const StockVehicleDocumentPage = lazy(() => import('@/pages/stock/StockVehicleDocumentPage').then(m => ({ default: m.StockVehicleDocumentPage })));
const WarehousesPage = lazy(() => import('@/pages/stock/WarehousesPage').then(m => ({ default: m.WarehousesPage })));
const InventoriesPage = lazy(() => import('@/pages/stock/InventoriesPage').then(m => ({ default: m.InventoriesPage })));
const InventoryPage = lazy(() => import('@/pages/stock/InventoryPage').then(m => ({ default: m.InventoryPage })));
const PaymentsPage = lazy(() => import('@/pages/payments/PaymentsPage').then(m => ({ default: m.PaymentsPage })));
const RecurringPaymentsPage = lazy(() => import('@/pages/payments/RecurringPaymentsPage').then(m => ({ default: m.RecurringPaymentsPage })));
const BankAccountsPage = lazy(() => import('@/pages/treasury/BankAccountsPage').then(m => ({ default: m.BankAccountsPage })));
const BankAccountDetailPage = lazy(() => import('@/pages/treasury/BankAccountDetailPage').then(m => ({ default: m.BankAccountDetailPage })));
const ChecksPage = lazy(() => import('@/pages/checks/ChecksPage').then(m => ({ default: m.ChecksPage })));
const TraitesPage = lazy(() => import('@/pages/traites/TraitesPage').then(m => ({ default: m.TraitesPage })));
const ChecksDraftsPage = lazy(() => import('@/pages/checks/ChecksDraftsPage').then(m => ({ default: m.ChecksDraftsPage })));
const WithholdingListPage = lazy(() => import('@/pages/withholding/WithholdingListPage').then(m => ({ default: m.WithholdingListPage })));
const WithholdingCertificatePage = lazy(() => import('@/pages/withholding/WithholdingCertificatePage').then(m => ({ default: m.WithholdingCertificatePage })));
const WithholdingDeclarationsPage = lazy(() => import('@/pages/withholding/WithholdingDeclarationsPage').then(m => ({ default: m.WithholdingDeclarationsPage })));
const PosPage = lazy(() => import('@/pages/pos/PosPage').then(m => ({ default: m.PosPage })));
const CaissePage = lazy(() => import('@/pages/caisse/CaissePage').then(m => ({ default: m.CaissePage })));
const CaisseSessionsPage = lazy(() => import('@/pages/caisse/CaisseSessionsPage').then(m => ({ default: m.CaisseSessionsPage })));
const CaisseSessionDetailPage = lazy(() => import('@/pages/caisse/CaisseSessionDetailPage').then(m => ({ default: m.CaisseSessionDetailPage })));
const CaisseRegistersPage = lazy(() => import('@/pages/caisse/CaisseRegistersPage').then(m => ({ default: m.CaisseRegistersPage })));
const PdfTemplatesPage = lazy(() => import('@/pages/settings/PdfTemplatesPage').then(m => ({ default: m.PdfTemplatesPage })));
const PosPrintersSettingsPage = lazy(() => import('@/pages/settings/PosPrintersSettingsPage').then(m => ({ default: m.PosPrintersSettingsPage })));
const SyncStatusPage = lazy(() => import('@/pages/settings/SyncStatusPage').then(m => ({ default: m.SyncStatusPage })));
const PdfTemplateEditorPage = lazy(() => import('@/pages/settings/PdfTemplateEditorPage').then(m => ({ default: m.PdfTemplateEditorPage })));
const NavigationPrefsPage = lazy(() => import('@/pages/settings/NavigationPrefsPage').then(m => ({ default: m.NavigationPrefsPage })));
const DatabaseImportPage = lazy(() => import('@/pages/settings/DatabaseImportPage').then(m => ({ default: m.DatabaseImportPage })));
const ResourcesPage = lazy(() => import('@/pages/resources/ResourcesPage').then(m => ({ default: m.ResourcesPage })));

// Storefront
const StorefrontHubPage = lazy(() => import('@/pages/storefront/StorefrontHubPage').then(m => ({ default: m.StorefrontHubPage })));
const StorefrontProductsPage = lazy(() => import('@/pages/storefront/products/ProductsPage').then(m => ({ default: m.StorefrontProductsPage })));
const StorefrontProductEditorPage = lazy(() => import('@/pages/storefront/products/ProductEditorPage').then(m => ({ default: m.StorefrontProductEditorPage })));
const StorefrontCategoriesPage = lazy(() => import('@/pages/storefront/categories/CategoriesPage').then(m => ({ default: m.StorefrontCategoriesPage })));
const StorefrontCustomersPage = lazy(() => import('@/pages/storefront/customers/CustomersPage').then(m => ({ default: m.StorefrontCustomersPage })));
const StorefrontCustomerDetailPage = lazy(() => import('@/pages/storefront/customers/CustomerDetailPage').then(m => ({ default: m.StorefrontCustomerDetailPage })));
const StorefrontOrdersPage = lazy(() => import('@/pages/storefront/orders/OrdersPage').then(m => ({ default: m.StorefrontOrdersPage })));
const StorefrontOrderDetailPage = lazy(() => import('@/pages/storefront/orders/OrderDetailPage').then(m => ({ default: m.StorefrontOrderDetailPage })));
const StoreSettingsPage = lazy(() => import('@/pages/storefront/settings/StoreSettingsPage').then(m => ({ default: m.StoreSettingsPage })));
const NotificationSoundsSettingsPage = lazy(() => import('@/pages/storefront/settings/NotificationSoundsSettingsPage').then(m => ({ default: m.NotificationSoundsSettingsPage })));
const StoreThemePage = lazy(() => import('@/pages/storefront/theme/ThemePage').then(m => ({ default: m.StoreThemePage })));
const StoreBannersPage = lazy(() => import('@/pages/storefront/banners/BannersPage').then(m => ({ default: m.StoreBannersPage })));
const StorePagesPage = lazy(() => import('@/pages/storefront/pages/PagesPage').then(m => ({ default: m.StorePagesPage })));
const StorePageEditorPage = lazy(() => import('@/pages/storefront/pages/PageEditorPage').then(m => ({ default: m.StorePageEditorPage })));
const StorefrontAnalyticsPage = lazy(() => import('@/pages/storefront/analytics/AnalyticsPage').then(m => ({ default: m.StorefrontAnalyticsPage })));
const CodCalculatorPage = lazy(() => import('@/pages/storefront/analytics/CodCalculatorPage').then(m => ({ default: m.CodCalculatorPage })));
const NotificationsPage = lazy(() => import('@/pages/storefront/notifications/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const AbandonedCartsPage = lazy(() => import('@/pages/storefront/carts/AbandonedCartsPage').then(m => ({ default: m.AbandonedCartsPage })));

// Formations
const FormationsPage = lazy(() => import('@/pages/formations/FormationsPage').then(m => ({ default: m.FormationsPage })));
const FormationViewPage = lazy(() => import('@/pages/formations/FormationsPage').then(m => ({ default: m.FormationViewPage })));

// Billing
const PaywallPage = lazy(() => import('@/pages/billing/PaywallPage').then(m => ({ default: m.PaywallPage })));
const BillingPage = lazy(() => import('@/pages/billing/BillingPage').then(m => ({ default: m.BillingPage })));

// Team / user management
const TeamSettingsPage = lazy(() => import('@/pages/settings/team').then(m => ({ default: m.TeamSettingsPage })));
const AcceptInvitePage = lazy(() => import('@/pages/AcceptInvitePage').then(m => ({ default: m.AcceptInvitePage })));

// ProSpace admin
const ProHubPage = lazy(() => import('@/pages/pro/ProHubPage').then(m => ({ default: m.ProHubPage })));
const ProCustomersPage = lazy(() => import('@/pages/pro/ProCustomersPage').then(m => ({ default: m.ProCustomersPage })));
const ProCustomerDetailPage = lazy(() => import('@/pages/pro/ProCustomerDetailPage').then(m => ({ default: m.ProCustomerDetailPage })));
const ProStatsPage = lazy(() => import('@/pages/pro/ProStatsPage').then(m => ({ default: m.ProStatsPage })));
const ProProductsPage = lazy(() => import('@/pages/pro/ProProductsPage').then(m => ({ default: m.ProProductsPage })));
const ProProductEditorPage = lazy(() => import('@/pages/pro/ProProductEditorPage').then(m => ({ default: m.ProProductEditorPage })));
const ProCategoriesPage = lazy(() => import('@/pages/pro/ProCategoriesPage').then(m => ({ default: m.ProCategoriesPage })));
const ProDevisListPage = lazy(() => import('@/pages/pro/ProDevisListPage').then(m => ({ default: m.ProDevisListPage })));
const ProCommandesListPage = lazy(() => import('@/pages/pro/ProCommandesListPage').then(m => ({ default: m.ProCommandesListPage })));
const ProCommandeDetailPage = lazy(() => import('@/pages/pro/ProCommandeDetailPage').then(m => ({ default: m.ProCommandeDetailPage })));
const ProWalletsPage = lazy(() => import('@/pages/pro/ProWalletsPage').then(m => ({ default: m.ProWalletsPage })));
const ProCommissionsPage = lazy(() => import('@/pages/pro/ProCommissionsPage').then(m => ({ default: m.ProCommissionsPage })));
const ProSettingsPage = lazy(() => import('@/pages/pro/ProSettingsPage').then(m => ({ default: m.ProSettingsPage })));

// queryClient lives in its own module so non-React code (optimistic
// builders, sync handlers) can read cached values synchronously.

// Local mirror of every successful query result, persisted to **IndexedDB**
// so the app behaves like a local-first database:
//
//   - Storage budget: practically unlimited (browsers commonly grant
//     hundreds of MB to IDB; localStorage caps at 5 MB and would
//     silently truncate a real-world ERP's data).
//   - Async writes: the persister batches via `throttleTime` so we
//     don't tie up the main thread on every mutation.
//   - Hydrated synchronously on app boot: the very first render of any
//     page that has a cached query gets the data immediately, without
//     a network round-trip.
//
// Failed/erroring queries are *not* persisted (the dehydrate filter
// below) — we don't want to teach the app that a 401/500 is the new
// truth and serve it offline.
// One-time migration: drop the old localStorage cache key from the v1
// persister so it doesn't sit around taking up the 5MB localStorage
// quota forever. Cheap, idempotent.
if (typeof window !== 'undefined') {
  try {
    window.localStorage.removeItem('erp.query-cache.v1');
  } catch {
    /* ignore — quota / private mode */
  }
}
const persister = createAsyncStoragePersister({
  storage: createQueryCacheStorage(),
  key: QUERY_CACHE_KEY,
  throttleTime: 1500,
});

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const hasAdminTenantViewExternalAuth = hasPlatformAdminTenantViewExternalAuth();
  // Tenant gate — a logged-in user without a tenant_member row is in
  // limbo and must finish the onboarding wizard before any business
  // page is reachable. We don't run this query until the user is
  // authenticated to avoid a needless 401 on logged-out visits.
  const tenantQuery = useCurrentTenant();
  const tenantLoading = (!!user || hasAdminTenantViewExternalAuth) && tenantQuery.isLoading;
  const readyBootHint = !isLoading && user ? readBootHintSync() : null;
  const hasReadyBootHint =
    !!user && readyBootHint?.kind === 'ready' && readyBootHint.userId === user.id;

  // After a fresh sign-in (especially right after the password-recovery
  // flow), `auth.uid()` on the Postgres connection can lag a beat
  // behind the React state — `useCurrentTenant` then resolves with
  // `null` even though the user *does* have a tenant_member row. Once
  // we observe that combination (auth resolved, query settled, no
  // tenant), trigger one explicit refetch before letting the /welcome
  // bounce fire. Also covers the case where the very first attempt
  // hit the 7s tenant-boot timeout (treated as an error) — without
  // this we'd bounce a real tenant-having user to /welcome just
  // because their first fetch was slow. The retry flag is keyed by
  // `user.id` so logging in as a different account starts fresh.
  const retriedForUserRef = useRef<string | null>(null);
  const tenantQuerySettled =
    !isLoading &&
    !!user &&
    !tenantQuery.isLoading &&
    !tenantQuery.isFetching &&
    (tenantQuery.status === 'success' || tenantQuery.status === 'error');
  const shouldRetryTenantOnce =
    tenantQuerySettled && !tenantQuery.data && retriedForUserRef.current !== (user?.id ?? null);
  useEffect(() => {
    if (!shouldRetryTenantOnce) return;
    retriedForUserRef.current = user?.id ?? null;
    void tenantQuery.refetch();
  }, [shouldRetryTenantOnce, tenantQuery, user?.id]);

  // Optimistic routing — but ONLY for the happy "ready" path. We used
  // to also redirect to /welcome / /login from stale hints to feel snappy,
  // but that briefly flashed the onboarding wizard on accounts that
  // already had a tenant (boot hint hadn't been refreshed yet, or was
  // from a different user). The cost of being wrong on the unhappy
  // paths is high (whole-page wrong-screen flash), the cost of being
  // right is just a splash for ~300ms — not worth the trade.
  if (isLoading && !hasAdminTenantViewExternalAuth) {
    return <BrandBootBlank />;
  }
  if (tenantLoading && hasReadyBootHint) {
    return <>{children}</>;
  }
  if (tenantLoading) {
    return <BrandBootBlank />;
  }
  if (!user && hasAdminTenantViewExternalAuth) {
    if (tenantQuery.isLoading || tenantQuery.isFetching) {
      return <BrandBootBlank />;
    }
    return <>{children}</>;
  }
  if (!user) return <Navigate to="/login" replace />;
  // If the no-tenant retry is queued or in flight, hold the render with
  // a splash rather than bouncing to /welcome. Same intent as
  // `tenantLoading` above — we just don't have all the signals visible
  // in `isFetching` yet on the first render after the effect schedules.
  if (shouldRetryTenantOnce || (tenantQuery.isFetching && !tenantQuery.data)) {
    if (hasReadyBootHint) {
      return <>{children}</>;
    }
    return <BrandBootBlank />;
  }
  if (tenantQuery.isError) {
    if (hasReadyBootHint) {
      return <>{children}</>;
    }
    return <Navigate to="/welcome" replace />;
  }
  if (!tenantQuery.data) {
    // No tenant loaded right now. Two scenarios:
    //  a) Genuinely no tenant (fresh signup, mid-onboarding) → /welcome.
    //  b) Tenant exists but the query failed because we're offline → must
    //     NOT bounce to /welcome (terrible UX, the user just loses access
    //     to all their data). The boot hint is the authoritative "did
    //     this user ever have a tenant" signal — if it says ready, we
    //     trust it.
    //
    // The previous version of this guard required `bridge.network.isOnline()
    // === false` AND `tenantQuery.isFetched === true`. That was too strict:
    // navigator.onLine on Windows can lie (returns true when DNS is dead),
    // and TanStack Query's `isFetched` flips back to false during
    // background refetches. Net result: the user got bounced to /welcome
    // anyway. We now trust the hint whenever it says ready and there's
    // no contradicting evidence (a fresh successful query that returned
    // null/undefined for THIS user).
    if (hasReadyBootHint) {
      // Hint says they had a tenant. The query came back empty or failed —
      // either way, render the dashboard with cached data. Worst case the
      // user reconnects, the query refreshes, and if they really have no
      // tenant the hint will be rewritten on the next mount.
      return (
        <InitialSyncGate fallback={(s) => <InitialSyncSplash status={s} />}>
          {children}
        </InitialSyncGate>
      );
    }
    return <Navigate to="/welcome" replace />;
  }
  return (
    <InitialSyncGate fallback={(s) => <InitialSyncSplash status={s} />}>
      {children}
    </InitialSyncGate>
  );
}

function PlatformAdminRoute({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const access = usePlatformAdminAccess(!!user);

  if (isLoading) return <BrandBootBlank />;
  if (!user) {
    return <Navigate to={isPlatformAdminHost() ? '/login' : '/platform/login'} replace />;
  }
  if (access.isLoading) return <PlatformAdminAccessLoading />;
  if (access.isError) {
    return <PlatformAdminAccessRetry onRetry={() => void access.refetch()} />;
  }
  if (access.data !== true) {
    return <PlatformAdminForbidden />;
  }
  return <>{children}</>;
}

function PlatformAdminAccessLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-md space-y-3 rounded-lg border bg-card p-6 shadow-sm">
        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
          <div className="h-full w-1/2 animate-pulse rounded-full bg-primary" />
        </div>
        <div>
          <h1 className="text-lg font-semibold">Verification admin</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Validation securisee de la session plateforme.
          </p>
        </div>
      </div>
    </div>
  );
}

function PlatformAdminAccessRetry({ onRetry }: { onRetry: () => void }) {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const signOut = async () => {
    await logout();
    navigate(isPlatformAdminHost() ? '/login' : '/platform/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-md space-y-4 rounded-lg border bg-card p-6 shadow-sm">
        <div>
          <h1 className="text-lg font-semibold">Chargement admin interrompu</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            La verification de l acces plateforme n a pas repondu. Relance sans rafraichir la page.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex h-9 items-center justify-center rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground"
          >
            Reessayer
          </button>
          <button
            type="button"
            onClick={signOut}
            className="inline-flex h-9 items-center justify-center rounded-full border px-4 text-sm font-semibold"
          >
            Changer de compte
          </button>
        </div>
      </div>
    </div>
  );
}

function PlatformAdminForbidden() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const signOut = async () => {
    await logout();
    navigate(isPlatformAdminHost() ? '/login' : '/platform/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-md space-y-4 rounded-lg border bg-card p-6 shadow-sm">
        <div>
          <h1 className="text-lg font-semibold">Acces plateforme refuse</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Le compte {user?.email ?? 'connecte'} n'est pas autorise a ouvrir
            la console admin Gesty.
          </p>
        </div>
        <button
          type="button"
          onClick={signOut}
          className="inline-flex h-9 items-center justify-center rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground"
        >
          Changer de compte
        </button>
      </div>
    </div>
  );
}

function Loading() {
  return <BrandBootBlank />;
}

// When the user clicks the password-recovery email link, Supabase JS
// auto-detects the token in the URL hash and emits a PASSWORD_RECOVERY
// event (captured into `isPasswordRecovery` by the auth context). We
// then force-redirect to /auth/update-password so they can't roam the
// app with a half-baked recovery session.
function PasswordRecoveryRedirect() {
  const { isPasswordRecovery } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isPasswordRecovery && location.pathname !== '/auth/update-password') {
      navigate('/auth/update-password', { replace: true });
    }
  }, [isPasswordRecovery, location.pathname, navigate]);

  return null;
}

function AppRoutes({ adminTenantViewActive }: { adminTenantViewActive: boolean }) {
  const { user, isLoading } = useAuth();
  const adminTenantViewExternalAuth = hasPlatformAdminTenantViewExternalAuth();
  const platformAdminMode = isPlatformAdminMode() && !adminTenantViewActive;
  const platformAdminHome = isPlatformAdminHost() ? '/' : '/platform';
  const platformAdminLogin = isPlatformAdminHost() ? '/login' : '/platform/login';

  if (isLoading && !adminTenantViewActive && !adminTenantViewExternalAuth) {
    return <BrandBootBlank />;
  }

  return (
    <Suspense fallback={<Loading />}>
      <PasswordRecoveryRedirect />
      <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to={platformAdminMode ? platformAdminHome : '/'} replace /> : <LoginPage />}
        />
        <Route
          path="/platform/login"
          element={user ? <Navigate to="/platform" replace /> : <LoginPage />}
        />
        <Route
          path="/signup"
          element={
            platformAdminMode
              ? <Navigate to={platformAdminLogin} replace />
              : user ? <Navigate to="/" replace /> : <SignupPage />
          }
        />
        {/* Invite acceptance — public route. The page handles three states:
            logged-out (signup form), logged-in matching email (one-click
            accept), logged-in wrong email (sign-out prompt). After accept,
            useCurrentTenant resolves immediately and ProtectedRoute lets
            the user straight to the dashboard, so OnboardingWizard never
            fires. */}
        <Route path="/invite/:token" element={<AcceptInvitePage />} />
        {/* Password reset — public routes (the recovery flow doesn't need
            an existing tenant; the user is in a recovery session, not a
            normal logged-in state). */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/update-password" element={<UpdatePasswordPage />} />
        {/* Onboarding wizard — accessible only to authenticated users
            who haven't yet created a tenant. Once the tenant exists
            the ProtectedRoute redirect rule kicks in elsewhere. */}
        <Route
          path="/welcome"
          element={
            platformAdminMode
              ? <Navigate to={platformAdminHome} replace />
              : user ? <OnboardingWizard /> : <Navigate to="/login" replace />
          }
        />
        {platformAdminMode ? (
          <>
            <Route
              element={
                <PlatformAdminRoute>
                  <PlatformAdminLayout />
                </PlatformAdminRoute>
              }
            >
              <Route path="/" element={<PlatformAdminDashboardPage />} />
              <Route path="/clients" element={<PlatformAdminClientsPage />} />
              <Route path="/revenue" element={<PlatformAdminRevenuePage />} />
              <Route path="/storefront" element={<PlatformAdminStorefrontPage />} />
              <Route path="/pro" element={<PlatformAdminProPage />} />
              <Route path="/risks" element={<PlatformAdminRisksPage />} />
              <Route path="/platform" element={<PlatformAdminDashboardPage />} />
              <Route path="/platform/clients" element={<PlatformAdminClientsPage />} />
              <Route path="/platform/revenue" element={<PlatformAdminRevenuePage />} />
              <Route path="/platform/storefront" element={<PlatformAdminStorefrontPage />} />
              <Route path="/platform/pro" element={<PlatformAdminProPage />} />
              <Route path="/platform/risks" element={<PlatformAdminRisksPage />} />
            </Route>
            <Route path="*" element={<Navigate to={platformAdminHome} replace />} />
          </>
        ) : (
          <>
        <Route
          path="/caisse/pos"
          element={
            <ProtectedRoute>
              <PosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/formations/view/:slug"
          element={
            <ProtectedRoute>
              <FormationViewPage />
            </ProtectedRoute>
          }
        />
        {/* Paywall — full-page (no sidebar). Rendered outside AppLayout so the
            blocked user can't reach the rest of the app. ProtectedRoute still
            requires login + onboarded tenant; the SubscriptionGate inside
            AppLayout is the one that REDIRECTS here when expired. */}
        <Route
          path="/paywall"
          element={
            <ProtectedRoute>
              <PaywallPage />
            </ProtectedRoute>
          }
        />
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<DashboardPage />} />
          <Route path="/articles" element={<ArticlesPage />} />
          <Route path="/articles/:id" element={<ArticleProfilePage />} />
          <Route path="/articles/categories" element={<CategoriesPage />} />
          <Route path="/articles/families" element={<FamiliesPage />} />
          <Route path="/articles/brands" element={<BrandsPage />} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/clients/:id" element={<ClientProfilePage />} />
          <Route path="/representatives" element={<RepresentativesPage />} />
          <Route path="/representatives/:id" element={<RepresentativeProfilePage />} />
          <Route path="/suppliers" element={<SuppliersPage />} />
          <Route path="/suppliers/:id" element={<SupplierProfilePage />} />

          {/* Ventes */}
          <Route path="/sales/quotes" element={<SalesDocumentList docType="quote" basePath="/sales/quotes" />} />
          <Route path="/sales/quotes/:id" element={<SalesDocumentPage docType="quote" basePath="/sales/quotes" />} />
          <Route path="/sales/orders" element={<SalesDocumentList docType="order" basePath="/sales/orders" />} />
          <Route path="/sales/orders/:id" element={<SalesDocumentPage docType="order" basePath="/sales/orders" />} />
          <Route path="/sales/deliveries" element={<SalesDocumentList docType="delivery" basePath="/sales/deliveries" />} />
          <Route path="/sales/deliveries/:id" element={<SalesDocumentPage docType="delivery" basePath="/sales/deliveries" />} />
          <Route path="/sales/invoices" element={<SalesDocumentList docType="invoice" basePath="/sales/invoices" />} />
          <Route path="/sales/invoices/:id" element={<SalesDocumentPage docType="invoice" basePath="/sales/invoices" />} />
          <Route path="/sales/credit-notes" element={<SalesDocumentList docType="credit_note" basePath="/sales/credit-notes" />} />
          <Route path="/sales/credit-notes/:id" element={<SalesDocumentPage docType="credit_note" basePath="/sales/credit-notes" />} />

          {/* Achats */}
          <Route path="/purchases/orders" element={<PurchaseDocumentList docType="order" basePath="/purchases/orders" />} />
          <Route path="/purchases/orders/:id" element={<PurchaseDocumentPage docType="order" basePath="/purchases/orders" />} />
          <Route path="/purchases/receptions" element={<PurchaseDocumentList docType="reception" basePath="/purchases/receptions" />} />
          <Route path="/purchases/receptions/:id" element={<PurchaseDocumentPage docType="reception" basePath="/purchases/receptions" />} />
          <Route path="/purchases/invoices" element={<PurchaseDocumentList docType="invoice" basePath="/purchases/invoices" />} />
          <Route path="/purchases/invoices/:id" element={<PurchaseDocumentPage docType="invoice" basePath="/purchases/invoices" />} />
          <Route path="/purchases/credit-notes" element={<PurchaseDocumentList docType="credit_note" basePath="/purchases/credit-notes" />} />
          <Route path="/purchases/credit-notes/:id" element={<PurchaseDocumentPage docType="credit_note" basePath="/purchases/credit-notes" />} />

          {/* Stock */}
          <Route path="/stock" element={<StockPage />} />
          <Route path="/stock/vehicle-exits" element={<StockVehicleDocumentsPage documentType="exit" />} />
          <Route path="/stock/vehicle-exits/new" element={<StockVehicleDocumentPage documentType="exit" />} />
          <Route path="/stock/vehicle-exits/:id" element={<StockVehicleDocumentPage documentType="exit" />} />
          <Route path="/stock/vehicle-entries" element={<StockVehicleDocumentsPage documentType="entry" />} />
          <Route path="/stock/vehicle-entries/new" element={<StockVehicleDocumentPage documentType="entry" />} />
          <Route path="/stock/vehicle-entries/:id" element={<StockVehicleDocumentPage documentType="entry" />} />
          <Route path="/stock/warehouses" element={<WarehousesPage />} />
          <Route path="/stock/inventories" element={<InventoriesPage />} />
          <Route path="/stock/inventories/new" element={<InventoryPage />} />
          <Route path="/stock/inventories/:id" element={<InventoryPage />} />

          {/* Paiements */}
          <Route path="/payments" element={<PaymentsPage />} />
          <Route path="/payments/recurring" element={<RecurringPaymentsPage />} />

          {/* Trésorerie */}
          <Route path="/treasury" element={<BankAccountsPage />} />
          <Route path="/treasury/:id" element={<BankAccountDetailPage />} />

          {/* Caisse — dashboard & sub-pages */}
          <Route path="/caisse" element={<CaissePage />} />
          <Route path="/caisse/registers" element={<CaisseRegistersPage />} />
          <Route path="/caisse/sessions" element={<CaisseSessionsPage />} />
          <Route path="/caisse/sessions/:id" element={<CaisseSessionDetailPage />} />

          {/* Chèques & Traites — split pages */}
          <Route path="/checks" element={<ChecksPage />} />
          <Route path="/traites" element={<TraitesPage />} />
          {/* Legacy combined page — kept for backward compat */}
          <Route path="/checks-drafts" element={<ChecksDraftsPage />} />

          {/* Retenue à la source */}
          <Route path="/withholding/sales" element={<WithholdingListPage certificateType="sale" />} />
          <Route path="/withholding/sales/declarations" element={<WithholdingDeclarationsPage certificateType="sale" />} />
          <Route path="/withholding/sales/declarations/new" element={<WithholdingDeclarationsPage certificateType="sale" />} />
          <Route path="/withholding/sales/:id" element={<WithholdingCertificatePage certificateType="sale" />} />
          <Route path="/withholding/purchases" element={<WithholdingListPage certificateType="purchase" />} />
          <Route path="/withholding/purchases/declarations" element={<WithholdingDeclarationsPage certificateType="purchase" />} />
          <Route path="/withholding/purchases/declarations/new" element={<WithholdingDeclarationsPage certificateType="purchase" />} />
          <Route path="/withholding/purchases/:id" element={<WithholdingCertificatePage certificateType="purchase" />} />

          {/* Storefront */}
          <Route path="/storefront" element={<StorefrontHubPage />} />
          <Route path="/storefront/products" element={<StorefrontProductsPage />} />
          <Route path="/storefront/products/:id" element={<StorefrontProductEditorPage />} />
          <Route path="/storefront/categories" element={<StorefrontCategoriesPage />} />
          <Route path="/storefront/customers" element={<StorefrontCustomersPage />} />
          <Route path="/storefront/customers/:id" element={<StorefrontCustomerDetailPage />} />
          <Route path="/storefront/orders" element={<StorefrontOrdersPage />} />
          <Route path="/storefront/orders/:id" element={<StorefrontOrderDetailPage />} />
          <Route path="/storefront/theme" element={<StoreThemePage />} />
          <Route path="/storefront/banners" element={<StoreBannersPage />} />
          <Route path="/storefront/pages" element={<StorePagesPage />} />
          <Route path="/storefront/pages/:id" element={<StorePageEditorPage />} />
          <Route path="/storefront/checkout" element={<Navigate to="/storefront/settings?tab=checkout" replace />} />
          <Route path="/storefront/settings" element={<StoreSettingsPage />} />
          <Route path="/storefront/settings/notification-sounds" element={<NotificationSoundsSettingsPage />} />
          <Route path="/storefront/analytics" element={<StorefrontAnalyticsPage />} />
          <Route path="/storefront/cod" element={<CodCalculatorPage />} />
          <Route path="/storefront/notifications" element={<NotificationsPage />} />
          <Route path="/storefront/abandoned-carts" element={<AbandonedCartsPage />} />

          {/* Formations (in-app training) — /formations/view/:slug is rendered
              standalone above AppLayout so it can be shared without chrome. */}
          <Route path="/formations" element={<FormationsPage />} />
          <Route path="/formations/:slug" element={<FormationsPage />} />

          {/* Paramètres */}
          <Route path="/settings" element={<SettingsHubPage />} />
          <Route path="/settings/company" element={<CompanySettingsPage />} />
          <Route path="/settings/pdf-templates" element={<PdfTemplatesPage />} />
          <Route path="/settings/pdf-templates/:id" element={<PdfTemplateEditorPage />} />
          <Route path="/settings/article-tickets" element={<ArticleTicketSettingsPage />} />
          <Route path="/settings/quick-links" element={<QuickLinksSettingsPage />} />
          <Route path="/settings/navigation" element={<NavigationPrefsPage />} />
          <Route path="/settings/modules" element={<ModulesSettingsPage />} />
          <Route path="/settings/branding" element={<BrandingPage />} />
          <Route path="/settings/app-builder" element={<AppBuilderPage />} />
          <Route path="/app-builder" element={<AppBuilderPage />} />
          <Route path="/settings/billing" element={<BillingPage />} />
          <Route
            path="/settings/team"
            element={
              <RequirePermission module="team" action="view">
                <TeamSettingsPage />
              </RequirePermission>
            }
          />
          <Route path="/settings/delivery-providers" element={<DeliveryProvidersListPage />} />
          <Route path="/settings/delivery-providers/:code" element={<DeliveryProviderProfilePage />} />
          <Route path="/settings/pos-printers" element={<PosPrintersSettingsPage />} />
          <Route path="/settings/sync" element={<SyncStatusPage />} />
          <Route path="/settings/database-import" element={<DatabaseImportPage />} />

          {/* ProSpace — gated by the `pro` module flag (tenant default + per-user
              override). Direct URL hits redirect to / for users without access. */}
          <Route path="/pro" element={<ModuleGuard module="pro"><ProHubPage /></ModuleGuard>} />
          <Route path="/pro/customers" element={<ModuleGuard module="pro"><ProCustomersPage /></ModuleGuard>} />
          <Route path="/pro/customers/:id" element={<ModuleGuard module="pro"><ProCustomerDetailPage /></ModuleGuard>} />
          <Route path="/pro/stats" element={<ModuleGuard module="pro"><ProStatsPage /></ModuleGuard>} />
          <Route path="/pro/products" element={<ModuleGuard module="pro"><ProProductsPage /></ModuleGuard>} />
          <Route path="/pro/products/:id" element={<ModuleGuard module="pro"><ProProductEditorPage /></ModuleGuard>} />
          <Route path="/pro/categories" element={<ModuleGuard module="pro"><ProCategoriesPage /></ModuleGuard>} />
          <Route path="/pro/devis" element={<ModuleGuard module="pro"><ProDevisListPage /></ModuleGuard>} />
          <Route path="/pro/commandes" element={<ModuleGuard module="pro"><ProCommandesListPage /></ModuleGuard>} />
          <Route path="/pro/commandes/:code" element={<ModuleGuard module="pro"><ProCommandeDetailPage /></ModuleGuard>} />
          <Route path="/pro/wallets" element={<ModuleGuard module="pro"><ProWalletsPage /></ModuleGuard>} />
          <Route path="/pro/commissions" element={<ModuleGuard module="pro"><ProCommissionsPage /></ModuleGuard>} />
          <Route path="/pro/settings" element={<ModuleGuard module="pro"><ProSettingsPage /></ModuleGuard>} />
          {/* legacy paths → still work, also gated */}
          <Route path="/pro/pricing" element={<ModuleGuard module="pro"><ProProductsPage /></ModuleGuard>} />
          <Route path="/settings/pro-customers" element={<ModuleGuard module="pro"><ProCustomersPage /></ModuleGuard>} />

          {/* Ressources (bibliothèque documents) */}
          <Route path="/resources" element={<ResourcesPage />} />
        </Route>
          </>
        )}
      </Routes>
    </Suspense>
  );
}

/**
 * Inner shell — runs *after* NativeBridgeProvider so we can pull
 * SecureStorage off the bridge and pass it to Supabase as the session
 * store. On web this is just a localStorage wrapper, on desktop it's
 * Stronghold + the OS keychain.
 */
function AppShell() {
  // Single Supabase project for everything now (multi-tenant via tenant_id).
  // The previous master/tenant split was abandoned in favour of one shared
  // master DB — see supabase/migrations/20260426010000_*.sql for context.
  const masterUrl = import.meta.env.VITE_SUPABASE_URL || '';
  const masterAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
  const bridge = useNativeBridge();
  const initialAdminTenantContext = useMemo(
    () => getInitialPlatformAdminTenantContext(),
    [],
  );
  const [adminTenantId, setAdminTenantId] = useState(initialAdminTenantContext.tenantId);
  const [adminHandoffToken, setAdminHandoffToken] = useState(
    initialAdminTenantContext.handoffToken,
  );
  const [adminAccessToken, setAdminAccessToken] = useState(
    initialAdminTenantContext.accessToken,
  );

  useEffect(() => {
    if (adminTenantId) queryClient.clear();
  }, [adminTenantId]);

  useEffect(() => {
    const handleAdminTenantViewCleared = () => {
      setAdminTenantId(undefined);
      setAdminHandoffToken(undefined);
      setAdminAccessToken(undefined);
      queryClient.clear();
    };
    window.addEventListener(
      ADMIN_TENANT_VIEW_SESSION_CLEARED_EVENT,
      handleAdminTenantViewCleared,
    );
    return () => {
      window.removeEventListener(
        ADMIN_TENANT_VIEW_SESSION_CLEARED_EVENT,
        handleAdminTenantViewCleared,
      );
    };
  }, []);

  useEffect(() => {
    const handleAdminTenantViewUrl = () => {
      const nextContext = readAndStorePlatformAdminTenantContextFromUrl();
      if (!nextContext.handoffToken) return;
      setAdminTenantId(undefined);
      setAdminHandoffToken(nextContext.handoffToken);
      setAdminAccessToken(nextContext.accessToken);
      queryClient.clear();
    };

    window.addEventListener('hashchange', handleAdminTenantViewUrl);
    window.addEventListener('popstate', handleAdminTenantViewUrl);
    return () => {
      window.removeEventListener('hashchange', handleAdminTenantViewUrl);
      window.removeEventListener('popstate', handleAdminTenantViewUrl);
    };
  }, []);

  const activateAdminTenantView = useCallback((tenantId: string, viewExpiresAt: string) => {
    storePlatformAdminTenantView(tenantId, viewExpiresAt);
    clearPlatformAdminHandoffToken();
    setAdminHandoffToken(undefined);
    setAdminTenantId(tenantId);
  }, []);

  const clearAdminHandoff = useCallback(() => {
    clearPlatformAdminHandoffToken();
    clearPlatformAdminAccessToken();
    setAdminHandoffToken(undefined);
    setAdminAccessToken(undefined);
  }, []);

  const services = useMemo(
    () => {
      if (!masterUrl || !masterAnonKey) return null;
      return createSupabaseServices({
        masterUrl,
        masterAnonKey,
        // Bridge-backed session storage. On web this is localStorage
        // (same as before); on desktop it's the Tauri Store (fast,
        // OS-protected app-data file) — Stronghold is reserved for
        // higher-value secrets and was too slow for per-keystroke
        // session writes. See session-storage-bridge.ts for details.
        sessionStorage: createSessionStorageBridge(
          bridge.preferences,
          bridge.secureStorage,
        ),
        // Intentionally NOT passing tenantUrl/tenantAnonKey: the data-service
        // falls back to master, which is exactly what we want.
        adminTenantId,
        adminAccessToken,
      });
    },
    [masterUrl, masterAnonKey, bridge.preferences, bridge.secureStorage, adminTenantId, adminAccessToken],
  );

  if (!services) {
    return <MissingRuntimeConfig />;
  }

  return (
    <ServiceProvider services={services}>
      <AuthProvider authService={services.authService}>
        <SyncProvider>
          <BrandThemeProvider initialPrimaryColor="#1f6fff" initialBorderRadius="0.625">
            <InitialSyncProvider>
              <BrowserRouter>
                <NavPrefsProvider>
                  <DesktopShellHooks />
                  <AppErrorBoundary>
                    <AdminTenantViewHandoffGate
                      token={adminHandoffToken}
                      services={services}
                      onTenantViewReady={activateAdminTenantView}
                      onTokenRejected={clearAdminHandoff}
                    >
                      <AppRoutes adminTenantViewActive={Boolean(adminTenantId)} />
                    </AdminTenantViewHandoffGate>
                  </AppErrorBoundary>
                </NavPrefsProvider>
              </BrowserRouter>
            </InitialSyncProvider>
          </BrandThemeProvider>
        </SyncProvider>
      </AuthProvider>
    </ServiceProvider>
  );
}

const ADMIN_TENANT_VIEW_STORAGE_KEY = 'gesty.adminTenantView';
const ADMIN_TENANT_HANDOFF_STORAGE_KEY = 'gesty.adminTenantHandoff';
const ADMIN_TENANT_AUTH_STORAGE_KEY = 'gesty.adminTenantAuth';
const TENANT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ADMIN_HANDOFF_TOKEN_PATTERN = /^[A-Za-z0-9_-]{32,128}$/;
const ADMIN_ACCESS_TOKEN_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const ADMIN_TENANT_VIEW_TTL_MS = 60 * 60 * 1000;
const ADMIN_HANDOFF_BOOT_TIMEOUT_MS = 12_000;

type StoredAdminTenantView = {
  tenantId: string;
  viewExpiresAt: string;
};

type StoredAdminTenantAuth = {
  accessToken: string;
  expiresAt: string;
};

function AdminTenantViewHandoffGate({
  token,
  services,
  onTenantViewReady,
  onTokenRejected,
  children,
}: {
  token: string | undefined;
  services: AppServices;
  onTenantViewReady: (tenantId: string, viewExpiresAt: string) => void;
  onTokenRejected: () => void;
  children: ReactNode;
}) {
  const [status, setStatus] = useState<'idle' | 'pending' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const processingTokenRef = useRef<string | null>(null);
  const servicesRef = useRef(services);
  const onTenantViewReadyRef = useRef(onTenantViewReady);

  useEffect(() => {
    servicesRef.current = services;
    onTenantViewReadyRef.current = onTenantViewReady;
  }, [services, onTenantViewReady]);

  useEffect(() => {
    if (!token) {
      processingTokenRef.current = null;
      setStatus('idle');
      setMessage(null);
      return;
    }
    if (processingTokenRef.current === token) return;

    let cancelled = false;
    processingTokenRef.current = token;
    setStatus('pending');
    withAdminTenantHandoffTimeout(
      servicesRef.current.authService.acceptPlatformAdminTenantViewHandoff(token),
    )
      .then((access) => {
        if (cancelled) return;
        if (!isTenantId(access.tenantId)) {
          throw new Error('Invalid admin tenant handoff response.');
        }
        onTenantViewReadyRef.current(access.tenantId, access.viewExpiresAt);
        setStatus('done');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('[platform-admin] tenant handoff failed', error);
        clearPlatformAdminHandoffToken();
        clearPlatformAdminAccessToken();
        setMessage(error instanceof Error ? error.message : 'Lien admin expire ou invalide.');
        setStatus('error');
      });

    return () => {
      cancelled = true;
      if (processingTokenRef.current === token) {
        processingTokenRef.current = null;
      }
    };
  }, [token]);

  if (token && (status === 'idle' || status === 'pending')) {
    return <BrandBootBlank />;
  }

  if (token && status === 'error') {
    return <AdminTenantViewHandoffError message={message} onClear={onTokenRejected} />;
  }

  return <>{children}</>;
}

function withAdminTenantHandoffTimeout<T>(promise: Promise<T>): Promise<T> {
  if (typeof window === 'undefined') return promise;

  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error('Lien admin expire ou indisponible. Relance l acces depuis admin.gesty.tn.'));
    }, ADMIN_HANDOFF_BOOT_TIMEOUT_MS);

    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function AdminTenantViewHandoffError({
  message,
  onClear,
}: {
  message: string | null;
  onClear: () => void;
}) {
  const navigate = useNavigate();
  const dismiss = () => {
    onClear();
    navigate('/', { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-md space-y-4 rounded-lg border bg-card p-6 shadow-sm">
        <div>
          <h1 className="text-lg font-semibold">Lien admin expire</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Ce lien securise ne peut plus ouvrir le tableau de bord client.
            Relance l acces depuis admin.gesty.tn.
          </p>
          {message ? (
            <p className="mt-3 rounded-md border bg-muted px-3 py-2 text-xs text-muted-foreground">
              {message}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="inline-flex h-9 items-center justify-center rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground"
        >
          Retour au tableau de bord
        </button>
      </div>
    </div>
  );
}

function getInitialPlatformAdminTenantContext(): {
  tenantId?: string;
  handoffToken?: string;
  accessToken?: string;
} {
  if (shouldStartPlatformAdminClean()) {
    clearPlatformAdminTenantViewSession();
    return {};
  }

  const urlContext = readAndStorePlatformAdminTenantContextFromUrl();
  const storedTenantView = readStoredAdminTenantView();
  const handoffToken = urlContext.handoffToken ?? readStoredAdminHandoffToken();
  const storedAuth = readStoredAdminTenantAuth();
  const tenantId = urlContext.tenantId ?? storedTenantView?.tenantId;
  const accessToken = urlContext.accessToken ?? (
    tenantId || handoffToken ? storedAuth?.accessToken : undefined
  );

  if (!tenantId && !handoffToken && !urlContext.accessToken) {
    clearPlatformAdminAccessToken();
  }

  return {
    tenantId,
    handoffToken,
    accessToken,
  };
}

function shouldStartPlatformAdminClean(): boolean {
  if (typeof window === 'undefined' || !isPlatformAdminHost()) return false;
  if (hasAdminTenantViewLaunchIntentInUrl()) return false;

  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
  if (normalizedPath !== '/login' && normalizedPath !== '/platform/login') {
    return isPlatformAdminConsolePath(normalizedPath);
  }
  return true;
}

function hasAdminTenantViewLaunchIntentInUrl(): boolean {
  if (typeof window === 'undefined') return false;
  const searchParams = new URLSearchParams(window.location.search);
  return (
    searchParams.has('admin_tenant_view') ||
    Boolean(readAdminHandoffTokenFromUrl()) ||
    Boolean(readAdminAccessTokenFromUrl())
  );
}

function isPlatformAdminConsolePath(path: string): boolean {
  return (
    path === '/' ||
    path === '/platform' ||
    path === '/clients' ||
    path === '/platform/clients' ||
    path === '/revenue' ||
    path === '/platform/revenue' ||
    path === '/storefront' ||
    path === '/platform/storefront' ||
    path === '/pro' ||
    path === '/platform/pro' ||
    path === '/risks' ||
    path === '/platform/risks'
  );
}

function readAndStorePlatformAdminTenantContextFromUrl(): {
  tenantId?: string;
  handoffToken?: string;
  accessToken?: string;
} {
  if (typeof window === 'undefined') return {};
  const tenantView = readAdminTenantViewFromUrl();
  if (tenantView) {
    storePlatformAdminTenantView(tenantView.tenantId, tenantView.viewExpiresAt);
    clearPlatformAdminHandoffToken();
  }

  const token = readAdminHandoffTokenFromUrl();
  if (token && !tenantView) {
    try {
      window.sessionStorage.setItem(ADMIN_TENANT_HANDOFF_STORAGE_KEY, token);
    } catch {
      // The URL hash still carries this page load when storage is restricted.
    }
  }

  const auth = readAdminAccessTokenFromUrl();
  if (auth && (tenantView || token)) {
    storePlatformAdminAccessToken(auth.accessToken, auth.expiresAt);
  }

  if (tenantView || token || auth) {
    scrubAdminTenantViewSecretsFromUrl();
  }

  return {
    tenantId: tenantView?.tenantId,
    handoffToken: tenantView ? undefined : token,
    accessToken: tenantView || token ? auth?.accessToken : undefined,
  };
}

function readAdminTenantViewFromUrl(): StoredAdminTenantView | undefined {
  const searchParams = new URLSearchParams(window.location.search);
  const queryTenantId = searchParams.get('admin_tenant_id');
  const queryExpiresAt = searchParams.get('admin_view_expires_at');
  if (isTenantId(queryTenantId)) {
    return {
      tenantId: queryTenantId,
      viewExpiresAt: normalizeAdminAccessExpiresAt(queryExpiresAt),
    };
  }

  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hashParams = new URLSearchParams(hash);
  const hashTenantId = hashParams.get('admin_tenant_id');
  const hashExpiresAt = hashParams.get('admin_view_expires_at');
  return isTenantId(hashTenantId)
    ? {
        tenantId: hashTenantId,
        viewExpiresAt: normalizeAdminAccessExpiresAt(hashExpiresAt),
      }
    : undefined;
}

function readAdminHandoffTokenFromUrl(): string | undefined {
  const searchParams = new URLSearchParams(window.location.search);
  const queryToken = searchParams.get('admin_handoff');
  if (isAdminHandoffToken(queryToken)) return queryToken;

  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hashParams = new URLSearchParams(hash);
  const hashToken = hashParams.get('admin_handoff');
  return isAdminHandoffToken(hashToken) ? hashToken : undefined;
}

function readAdminAccessTokenFromUrl(): StoredAdminTenantAuth | undefined {
  const searchParams = new URLSearchParams(window.location.search);
  const queryToken = searchParams.get('admin_access_token');
  const queryExpiresAt = searchParams.get('admin_access_expires_at');
  if (isAdminAccessToken(queryToken)) {
    return {
      accessToken: queryToken,
      expiresAt: normalizeAdminAccessExpiresAt(queryExpiresAt),
    };
  }

  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hashParams = new URLSearchParams(hash);
  const hashToken = hashParams.get('admin_access_token');
  const hashExpiresAt = hashParams.get('admin_access_expires_at');
  return isAdminAccessToken(hashToken)
    ? {
        accessToken: hashToken,
        expiresAt: normalizeAdminAccessExpiresAt(hashExpiresAt),
      }
    : undefined;
}

function storePlatformAdminTenantView(tenantId: string, viewExpiresAt: string) {
  if (typeof window === 'undefined' || !isTenantId(tenantId)) return;
  const expiresAt = isFutureTimestamp(viewExpiresAt)
    ? viewExpiresAt
    : getDefaultAdminTenantViewExpiresAt();
  try {
    window.sessionStorage.setItem(
      ADMIN_TENANT_VIEW_STORAGE_KEY,
      JSON.stringify({ tenantId, viewExpiresAt: expiresAt } satisfies StoredAdminTenantView),
    );
  } catch {
    // Ignore restricted storage; the current render still receives tenantId.
  }
}

function readStoredAdminTenantView(): StoredAdminTenantView | undefined {
  try {
    const persisted = window.sessionStorage.getItem(ADMIN_TENANT_VIEW_STORAGE_KEY);
    if (!persisted) return undefined;

    if (isTenantId(persisted)) {
      const legacyView = {
        tenantId: persisted,
        viewExpiresAt: getDefaultAdminTenantViewExpiresAt(),
      };
      storePlatformAdminTenantView(legacyView.tenantId, legacyView.viewExpiresAt);
      return legacyView;
    }

    const parsed = JSON.parse(persisted) as Partial<StoredAdminTenantView>;
    if (isTenantId(parsed.tenantId) && isFutureTimestamp(parsed.viewExpiresAt)) {
      return {
        tenantId: parsed.tenantId,
        viewExpiresAt: parsed.viewExpiresAt,
      };
    }

    window.sessionStorage.removeItem(ADMIN_TENANT_VIEW_STORAGE_KEY);
    return undefined;
  } catch {
    return undefined;
  }
}

function storePlatformAdminAccessToken(accessToken: string, expiresAt: string) {
  if (typeof window === 'undefined' || !isAdminAccessToken(accessToken)) return;
  const safeExpiresAt = isFutureTimestamp(expiresAt)
    ? expiresAt
    : getDefaultAdminTenantViewExpiresAt();
  try {
    window.sessionStorage.setItem(
      ADMIN_TENANT_AUTH_STORAGE_KEY,
      JSON.stringify({ accessToken, expiresAt: safeExpiresAt } satisfies StoredAdminTenantAuth),
    );
  } catch {
    // Ignore restricted storage; the current render still receives accessToken.
  }
}

function readStoredAdminTenantAuth(): StoredAdminTenantAuth | undefined {
  try {
    const persisted = window.sessionStorage.getItem(ADMIN_TENANT_AUTH_STORAGE_KEY);
    if (!persisted) return undefined;
    const parsed = JSON.parse(persisted) as Partial<StoredAdminTenantAuth>;
    if (isAdminAccessToken(parsed.accessToken) && isFutureTimestamp(parsed.expiresAt)) {
      return {
        accessToken: parsed.accessToken,
        expiresAt: parsed.expiresAt,
      };
    }

    window.sessionStorage.removeItem(ADMIN_TENANT_AUTH_STORAGE_KEY);
    return undefined;
  } catch {
    return undefined;
  }
}

function readStoredAdminHandoffToken(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const persisted = window.sessionStorage.getItem(ADMIN_TENANT_HANDOFF_STORAGE_KEY);
    return isAdminHandoffToken(persisted) ? persisted : undefined;
  } catch {
    return undefined;
  }
}

function clearPlatformAdminHandoffToken() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(ADMIN_TENANT_HANDOFF_STORAGE_KEY);
  } catch {
    // Ignore restricted storage.
  }
}

function clearPlatformAdminAccessToken() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(ADMIN_TENANT_AUTH_STORAGE_KEY);
  } catch {
    // Ignore restricted storage.
  }
}

function scrubAdminTenantViewSecretsFromUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  let changed = false;

  for (const key of [
    'admin_handoff',
    'admin_tenant_id',
    'admin_view_expires_at',
    'admin_access_token',
    'admin_access_expires_at',
  ]) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }

  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  if (hash) {
    const hashParams = new URLSearchParams(hash);
    for (const key of [
      'admin_handoff',
      'admin_tenant_id',
      'admin_view_expires_at',
      'admin_access_token',
      'admin_access_expires_at',
    ]) {
      if (hashParams.has(key)) {
        hashParams.delete(key);
        changed = true;
      }
    }
    const nextHash = hashParams.toString();
    url.hash = nextHash ? `#${nextHash}` : '';
  }

  if (changed) {
    window.history.replaceState(window.history.state, '', url.toString());
  }
}

function isTenantId(value: string | null | undefined): value is string {
  return typeof value === 'string' && TENANT_ID_PATTERN.test(value);
}

function isAdminHandoffToken(value: string | null | undefined): value is string {
  return typeof value === 'string' && ADMIN_HANDOFF_TOKEN_PATTERN.test(value);
}

function isAdminAccessToken(value: string | null | undefined): value is string {
  return (
    typeof value === 'string' &&
    value.length >= 64 &&
    value.length <= 4096 &&
    ADMIN_ACCESS_TOKEN_PATTERN.test(value)
  );
}

function isFutureTimestamp(value: string | null | undefined): value is string {
  if (!value) return false;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && timestamp > Date.now();
}

function getDefaultAdminTenantViewExpiresAt(): string {
  return new Date(Date.now() + ADMIN_TENANT_VIEW_TTL_MS).toISOString();
}

function normalizeAdminAccessExpiresAt(value: string | null | undefined): string {
  if (!value) return getDefaultAdminTenantViewExpiresAt();
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds > Date.now() / 1000) {
    return new Date(seconds * 1000).toISOString();
  }
  return isFutureTimestamp(value) ? value : getDefaultAdminTenantViewExpiresAt();
}
function MissingRuntimeConfig() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-md space-y-3 rounded-lg border bg-card p-6 shadow-sm">
        <h1 className="text-lg font-semibold">Configuration desktop manquante</h1>
        <p className="text-sm text-muted-foreground">
          Le bundle a ete genere sans VITE_SUPABASE_URL ou VITE_SUPABASE_ANON_KEY.
          Rebuild l'application avec le fichier apps/erp-web/.env avant de la lancer.
        </p>
      </div>
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          // Drop entries older than 24h on hydration so we don't show
          // stale data older than the gcTime contract above.
          maxAge: 24 * 60 * 60 * 1000,
          // Only persist queries that resolved successfully — mutations,
          // errors, and pending fetches stay out of the cache.
          dehydrateOptions: {
            shouldDehydrateQuery: (query) => query.state.status === 'success',
          },
        }}
        >
          <NativeBridgeProvider>
            <AppShell />
          </NativeBridgeProvider>
        </PersistQueryClientProvider>
      </I18nextProvider>
    </QueryClientProvider>
  );
}
